/* global femcare */

/**
 * Modal module for initializing generic modal functionality.
 * Uses delegated event handling for dynamically added triggers (quickBuyModal)
 * while allowing coexistence with other event listeners.
 */
femcare.modal = {
    isDelegatedListenerBound: false,
    modalElements: document.querySelectorAll('.modal'),
    lastOpenedTrigger: null,

    /**
     * Initializes modal logic.
     */
    init() {
        if (!this.isDelegatedListenerBound) {
            document.addEventListener('click', this.handleDelegatedModalTrigger.bind(this));
            this.isDelegatedListenerBound = true;
        }

        this.bindCloseListenersToModals();
    },

    /**
     * Handles delegated clicks on elements with data-modal-target.
     * @param {MouseEvent} event
     */
    handleDelegatedModalTrigger(event) {
        const clickedTrigger = event.target.closest('[data-modal-target]');
        if (!clickedTrigger) {
            return;
        }

        const modalId = clickedTrigger.getAttribute('data-modal-target');
        const modalToShow = document.getElementById(modalId);
        if (!modalToShow) {
            return;
        }

        if (modalToShow.classList.contains('modal--navigation')) {
            return;
        }

        this.closeAllOpenModals();
        this.lastOpenedTrigger = clickedTrigger;
        modalToShow.classList.add('displayed');

        const focusableInModal = modalToShow.querySelectorAll(femcare.shared.util.focusableSelectors);
        if (focusableInModal.length) {
            if (modalToShow.classList.contains('modal--search')) {
                const searchInput = modalToShow.querySelector('input[type="text"]');
                searchInput.focus();
            } else {
                focusableInModal[0].focus();
            }
        }

        this.trapFocus(modalToShow);
    },

    /**
     * Binds listeners to close buttons and backdrop click on all modals.
     */
    bindCloseListenersToModals() {
        this.modalElements = document.querySelectorAll('.modal');

        this.modalElements.forEach((modal) => {
            const dismissButtons = modal.querySelectorAll('[data-dismiss="modal"]');
            dismissButtons.forEach((button) => {
                button.addEventListener('click', () => this.closeModal(modal));
            });

            modal.addEventListener('click', (event) => {
                const modalDialog = modal.querySelector('.modal__dialog');
                if (event.target === modal || event.target === modalDialog) {
                    if (!modal.classList.contains("paywall")) {
                        this.closeModal(modal);
                    }
                }
            });
        });
    },

    /**
     * Closes the provided modal and returns focus to the trigger.
     * @param {HTMLElement} modal
     */
    closeModal(modal) {
        if (modal.classList.contains('modal--navigation')) {
            femcare.navigation.navigationMain?.closeNavigationModal();
            return;
        }

        modal.classList.remove('displayed');

        if (modal._trapFocusHandler) {
            modal.removeEventListener('keydown', modal._trapFocusHandler);
            delete modal._trapFocusHandler;
        }

        if (this.lastOpenedTrigger) {
            this.lastOpenedTrigger.focus();
            this.lastOpenedTrigger = null;
        }
    },

    /**
     * Closes all modals currently open.
     */
    closeAllOpenModals() {
        this.modalElements.forEach((modal) => {
            if (modal.classList.contains('displayed') || modal.classList.contains('modal--navigation-open')) {
                this.closeModal(modal);
            }
        });
    },

    /**
     * Traps keyboard focus within the modal and allows Escape to close.
     * @param {HTMLElement} modal
     */
    trapFocus(modal) {
        if (modal._trapFocusHandler) {
            return;
        }

        const focusableSelector = femcare.shared.util.focusableSelectors;
        const getFocusableChildren = () =>
            Array.from(modal.querySelectorAll(focusableSelector))
                .filter(el => el.offsetParent !== null && !el.disabled);

        const trapHandler = (e) => {
            const focusable = getFocusableChildren();
            if (!focusable.length) {
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;

            if (e.key === 'Tab') {
                if (e.shiftKey) {
                    if (active === first || !modal.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                } else if (active === last || !modal.contains(active)) {
                    e.preventDefault();
                    first.focus();
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.closeModal(modal);
            }
        };

        modal._trapFocusHandler = trapHandler;
        modal.addEventListener('keydown', trapHandler);

        if (!modal.hasAttribute('tabindex')) {
            modal.setAttribute('tabindex', '-1');
        }

        setTimeout(() => {
            const focusable = getFocusableChildren();
            if (focusable.length) {
                if (modal.classList.contains('modal--search')) {
                    const searchInput = modal.querySelector('input[type="text"]');
                    searchInput.focus();
                } else {
                    focusable[0].focus();
                }
            } else {
                modal.focus();
            }
        }, 10);
    },

    /**
     * Resets the navigation bar icons to their default state.
     */
    resetNavigationBar() {
        const openBtn = document.querySelector('.navigation-bar__icon--open');
        const closeBtn = document.querySelector('.navigation-bar__icon--close');

        openBtn?.classList.remove('hidden');
        closeBtn?.classList.add('hidden');
    },
};
